'use client';
import React, { useState } from 'react';

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  preferredContact: string;
  subject: string;
  message: string;
  callbackTime: string;
  consent: boolean;
}

interface FormErrors {
  fullName?: string;
  email?: string;
  phone?: string;
  message?: string;
  consent?: string;
}

const ContactSection: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    phone: '',
    preferredContact: 'phone',
    subject: '',
    message: '',
    callbackTime: '',
    consent: false,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required.';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address.';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required.';
    } else if (!/^[\d\s\-\+\(\)]{7,}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number.';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required.';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Please provide a more detailed message (at least 10 characters).';
    }

    if (!formData.consent) {
      newErrors.consent = 'You must agree to be contacted to submit this form.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          submittedAt: new Date().toISOString(),
          to: 'support@capsnicorks.com',
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          preferredContact: 'phone',
          subject: '',
          message: '',
          callbackTime: '',
          consent: false,
        });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    fontFamily: 'Poppins, sans-serif',
    fontSize: '14px',
    color: '#122658',
    backgroundColor: '#ffffff',
    border: '1px solid #dee2e6',
    borderRadius: '5px',
    padding: '12px 16px',
    width: '100%',
    outline: 'none',
    transition: 'border-color 0.2s',
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: 'Poppins, sans-serif',
    fontSize: '13px',
    fontWeight: 600,
    color: '#122658',
    display: 'block',
    marginBottom: '6px',
  };

  const errorStyle: React.CSSProperties = {
    fontFamily: 'Poppins, sans-serif',
    fontSize: '12px',
    color: '#C11E1F',
    marginTop: '4px',
  };

  return (
    <section
      id="contact"
      className="py-24"
      style={{ backgroundColor: '#122658' }}
      aria-label="Contact Caps N Corks II"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span
            className="inline-block text-sm uppercase tracking-widest font-medium mb-3"
            style={{ fontFamily: 'Poppins, sans-serif', color: '#387EF7', letterSpacing: '3px' }}
          >
            Get in Touch
          </span>
          <h2
            className="mb-6"
            style={{
              fontFamily: 'Roboto, sans-serif',
              fontWeight: 700,
              fontSize: 'clamp(28px, 3.5vw, 42px)',
              color: '#ffffff',
              lineHeight: 1.2,
            }}
          >
            Contact Caps N Corks II, LLC
          </h2>
          <div className="w-16 h-1 mx-auto mb-6" style={{ backgroundColor: '#387EF7' }} />
          <p
            className="max-w-2xl mx-auto"
            style={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: '16px',
              color: 'rgba(255,255,255,0.75)',
              lineHeight: 1.8,
            }}
          >
            Ready to connect? Fill out the form below to request a callback or send us an inquiry. We look forward to hearing from you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-1">
            <div
              className="rounded-lg p-8 h-full"
              style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
            >
              <h3
                className="mb-8"
                style={{
                  fontFamily: 'Roboto, sans-serif',
                  fontWeight: 700,
                  fontSize: '20px',
                  color: '#ffffff',
                }}
              >
                Our Information
              </h3>

              {/* Address */}
              <div className="flex items-start gap-4 mb-6">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: 'rgba(56,126,247,0.2)' }}
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 1C6.24 1 4 3.24 4 6c0 3.75 5 11 5 11s5-7.25 5-11c0-2.76-2.24-5-5-5zm0 6.5A1.5 1.5 0 117.5 6 1.5 1.5 0 019 7.5z" fill="#387EF7"/>
                  </svg>
                </div>
                <div>
                  <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px', fontWeight: 600, color: '#ffffff', marginBottom: '4px' }}>Address</p>
                  <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>
                    Caps N Corks II, LLC<br />
                    2974 Montila Drive<br />
                    Jacksonville, FL 32246<br />
                    USA
                  </p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start gap-4 mb-6">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: 'rgba(56,126,247,0.2)' }}
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3.6 7.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V17c0 .6-.4 1-1 1C8.6 18 0 9.4 0 1c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L3.6 7.8z" fill="#387EF7"/>
                  </svg>
                </div>
                <div>
                  <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px', fontWeight: 600, color: '#ffffff', marginBottom: '4px' }}>Phone</p>
                  <a
                    href="tel:+14083873442"
                    style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: '#387EF7', textDecoration: 'none' }}
                    aria-label="Call Caps N Corks II at (408) 387-3442"
                  >
                    (408) 387-3442
                  </a>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start gap-4 mb-8">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: 'rgba(56,126,247,0.2)' }}
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 2H2C.9 2 0 2.9 0 4v10c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 4l-7 4.5L2 6V4l7 4.5L16 4v2z" fill="#387EF7"/>
                  </svg>
                </div>
                <div>
                  <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px', fontWeight: 600, color: '#ffffff', marginBottom: '4px' }}>Email</p>
                  <a
                    href="mailto:support@capsnicorks.com"
                    style={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px', color: '#387EF7', textDecoration: 'none', wordBreak: 'break-all' }}
                    aria-label="Email Caps N Corks II"
                  >
                    support@capsnicorks.com
                  </a>
                </div>
              </div>

              {/* Map placeholder */}
              <div
                className="rounded-lg overflow-hidden"
                style={{ height: '160px', backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
              >
                <iframe
                  title="Caps N Corks II, LLC location map - 2974 Montila Drive, Jacksonville, FL 32246"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3451.0!2d-81.5!3d30.3!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2s2974+Montila+Drive%2C+Jacksonville%2C+FL+32246!5e0!3m2!1sen!2sus!4v1"
                  width="100%"
                  height="160"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div
              className="rounded-lg p-8"
              style={{ backgroundColor: '#ffffff', boxShadow: '4px 8px 30px rgba(0,0,0,0.2)' }}
            >
              <h3
                className="mb-8"
                style={{
                  fontFamily: 'Roboto, sans-serif',
                  fontWeight: 700,
                  fontSize: '22px',
                  color: '#122658',
                }}
              >
                Request a Callback
              </h3>

              {/* Success Message */}
              {submitStatus === 'success' && (
                <div
                  className="mb-6 p-4 rounded-lg"
                  style={{ backgroundColor: '#f0fdf4', border: '1px solid #86efac' }}
                  role="alert"
                  aria-live="polite"
                >
                  <div className="flex items-start gap-3">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0 mt-0.5">
                      <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" fill="#16a34a"/>
                    </svg>
                    <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: '#15803d', lineHeight: 1.6 }}>
                      <strong>Thank you for contacting Caps N Corks II, LLC.</strong> Your request has been received. A member of our team will contact you shortly.
                    </p>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {submitStatus === 'error' && (
                <div
                  className="mb-6 p-4 rounded-lg"
                  style={{ backgroundColor: '#fef2f2', border: '1px solid #fca5a5' }}
                  role="alert"
                  aria-live="polite"
                >
                  <div className="flex items-start gap-3">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0 mt-0.5">
                      <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" fill="#dc2626"/>
                    </svg>
                    <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: '#dc2626', lineHeight: 1.6 }}>
                      We were unable to submit your request. Please try again or contact us directly at{' '}
                      <a href="tel:+14083873442" style={{ color: '#dc2626', fontWeight: 600 }}>(408) 387-3442</a>.
                    </p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate aria-label="Contact and callback request form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                  {/* Full Name */}
                  <div>
                    <label htmlFor="fullName" style={labelStyle}>
                      Full Name <span style={{ color: '#C11E1F' }}>*</span>
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="Your full name"
                      style={{
                        ...inputStyle,
                        borderColor: errors.fullName ? '#C11E1F' : '#dee2e6',
                      }}
                      aria-required="true"
                      aria-describedby={errors.fullName ? 'fullName-error' : undefined}
                      aria-invalid={!!errors.fullName}
                    />
                    {errors.fullName && (
                      <p id="fullName-error" style={errorStyle} role="alert">{errors.fullName}</p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" style={labelStyle}>
                      Email Address <span style={{ color: '#C11E1F' }}>*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      style={{
                        ...inputStyle,
                        borderColor: errors.email ? '#C11E1F' : '#dee2e6',
                      }}
                      aria-required="true"
                      aria-describedby={errors.email ? 'email-error' : undefined}
                      aria-invalid={!!errors.email}
                    />
                    {errors.email && (
                      <p id="email-error" style={errorStyle} role="alert">{errors.email}</p>
                    )}
                  </div>

                  {/* Phone */}
                  <div>
                    <label htmlFor="phone" style={labelStyle}>
                      Phone Number <span style={{ color: '#C11E1F' }}>*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(000) 000-0000"
                      style={{
                        ...inputStyle,
                        borderColor: errors.phone ? '#C11E1F' : '#dee2e6',
                      }}
                      aria-required="true"
                      aria-describedby={errors.phone ? 'phone-error' : undefined}
                      aria-invalid={!!errors.phone}
                    />
                    {errors.phone && (
                      <p id="phone-error" style={errorStyle} role="alert">{errors.phone}</p>
                    )}
                  </div>

                  {/* Preferred Contact */}
                  <div>
                    <label htmlFor="preferredContact" style={labelStyle}>
                      Preferred Contact Method
                    </label>
                    <select
                      id="preferredContact"
                      name="preferredContact"
                      value={formData.preferredContact}
                      onChange={handleChange}
                      style={inputStyle}
                    >
                      <option value="phone">Phone</option>
                      <option value="email">Email</option>
                    </select>
                  </div>

                  {/* Subject */}
                  <div className="sm:col-span-2">
                    <label htmlFor="subject" style={labelStyle}>
                      Subject / Reason for Inquiry
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      style={inputStyle}
                    >
                      <option value="">Select a subject...</option>
                      <option value="general">General Inquiry</option>
                      <option value="publishing">Publishing Services</option>
                      <option value="editorial">Editorial Content</option>
                      <option value="digital">Digital Publications</option>
                      <option value="partnership">Partnership Opportunity</option>
                      <option value="callback">Request a Callback</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  {/* Message */}
                  <div className="sm:col-span-2">
                    <label htmlFor="message" style={labelStyle}>
                      Message / Request <span style={{ color: '#C11E1F' }}>*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Please describe your inquiry or request..."
                      rows={5}
                      style={{
                        ...inputStyle,
                        borderColor: errors.message ? '#C11E1F' : '#dee2e6',
                        resize: 'vertical',
                      }}
                      aria-required="true"
                      aria-describedby={errors.message ? 'message-error' : undefined}
                      aria-invalid={!!errors.message}
                    />
                    {errors.message && (
                      <p id="message-error" style={errorStyle} role="alert">{errors.message}</p>
                    )}
                  </div>

                  {/* Callback Time */}
                  <div className="sm:col-span-2">
                    <label htmlFor="callbackTime" style={labelStyle}>
                      Preferred Callback Time <span style={{ fontWeight: 400, color: '#7F7F90' }}>(optional)</span>
                    </label>
                    <select
                      id="callbackTime"
                      name="callbackTime"
                      value={formData.callbackTime}
                      onChange={handleChange}
                      style={inputStyle}
                    >
                      <option value="">Select a preferred time...</option>
                      <option value="morning">Morning (9am - 12pm EST)</option>
                      <option value="afternoon">Afternoon (12pm - 5pm EST)</option>
                      <option value="evening">Evening (5pm - 7pm EST)</option>
                      <option value="anytime">Anytime</option>
                    </select>
                  </div>

                  {/* Consent */}
                  <div className="sm:col-span-2">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="consent"
                        name="consent"
                        checked={formData.consent}
                        onChange={handleChange}
                        className="mt-1 flex-shrink-0"
                        style={{ width: '16px', height: '16px', accentColor: '#0066CC' }}
                        aria-required="true"
                        aria-describedby={errors.consent ? 'consent-error' : undefined}
                        aria-invalid={!!errors.consent}
                      />
                      <label
                        htmlFor="consent"
                        style={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px', color: '#7F7F90', lineHeight: 1.6, cursor: 'pointer' }}
                      >
                        I agree to be contacted by Caps N Corks II, LLC regarding my inquiry. I understand my information will be used solely to respond to my request. <span style={{ color: '#C11E1F' }}>*</span>
                      </label>
                    </div>
                    {errors.consent && (
                      <p id="consent-error" style={{ ...errorStyle, marginTop: '6px' }} role="alert">{errors.consent}</p>
                    )}
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-10 py-4 text-white font-medium uppercase tracking-widest transition-all duration-200 hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: '#0066CC',
                    borderRadius: '7px',
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '13px',
                    letterSpacing: '2px',
                  }}
                  aria-label="Submit callback request form"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="8" cy="8" r="6" stroke="rgba(255,255,255,0.3)" strokeWidth="2"/>
                        <path d="M8 2a6 6 0 016 6" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    'Request a Callback'
                  )}
                </button>
                <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', color: '#7F7F90', marginTop: '12px' }}>
                  * Required fields. Your information is kept confidential and will only be used to respond to your inquiry.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
